import { DatePicker } from "@/components/generic/DatePicker";
import { NumberField } from "@/components/generic/NumberField";
import { Select } from "@/components/generic/Select";
import { FilterIdentifier } from "@/data/filters/config";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { QueryFilter } from "@/types/campaign";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateValue, parseDate } from "@internationalized/date";
import { upperFirst } from "lodash-es";
import { useEffect, useMemo, useRef } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";
import { SelectItem } from "../generic/Select";

// Define time units
const timeUnits = [
  { id: "days", label: "dage siden" },
  { id: "months", label: "måneder siden" },
  { id: "years", label: "år siden" },
];

const schema = z
  .object({
    // Relative mode fields
    greaterThanValue: z.number().min(1, "Skal være mindst én").nullable(),
    lessThanValue: z.number().min(1, "Skal være mindst én").nullable(),
    timeUnit: z.enum(["days", "months", "years"]).default("days"),

    // Absolute date mode fields
    afterDate: z.custom<DateValue>().nullable(),
    beforeDate: z.custom<DateValue>().nullable(),
  })
  .refine(
    (data) => {
      // Ensure that the greaterThanValue is less than lessThanValue
      return (
        data.greaterThanValue == null ||
        data.lessThanValue == null ||
        data.greaterThanValue < data.lessThanValue
      );
    },
    {
      path: ["greaterThanValue"],
      message: 'Skal være mindre end "mindre end"',
    },
  )
  .refine(
    (data) => {
      // Ensure that lessThanValue is greater than greaterThanValue
      return (
        data.lessThanValue == null ||
        data.greaterThanValue == null ||
        data.lessThanValue > data.greaterThanValue
      );
    },
    {
      path: ["lessThanValue"],
      message: 'Skal være større end "mere end"',
    },
  );

export function DateFilter({
  group: { ...props },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const dateFilter = queryFilters.value;

  // Parse existing filter to form values
  const getDefaultValues = () => {
    const defaultValues: z.infer<typeof schema> = {
      greaterThanValue: null,
      lessThanValue: null,
      timeUnit: "days",
      afterDate: null,
      beforeDate: null,
    };

    if (!dateFilter) return defaultValues;

    switch (dateFilter.operator) {
      case "MoreThanDaysAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(dateFilter.values?.[0]),
          timeUnit: "days" as const,
        };
      case "MoreThanMonthsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(dateFilter.values?.[0]),
          timeUnit: "months" as const,
        };
      case "MoreThanYearsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(dateFilter.values?.[0]),
          timeUnit: "years" as const,
        };
      case "LessThanDaysAgo":
        return {
          ...defaultValues,
          lessThanValue: Number(dateFilter.values?.[0]),
          timeUnit: "days" as const,
        };
      case "LessThanMonthsAgo":
        return {
          ...defaultValues,
          lessThanValue: Number(dateFilter.values?.[0]),
          timeUnit: "months" as const,
        };
      case "LessThanYearsAgo":
        return {
          ...defaultValues,
          lessThanValue: Number(dateFilter.values?.[0]),
          timeUnit: "years" as const,
        };
      case "Between":
        return {
          ...defaultValues,
          afterDate: dateFilter.values ? parseDate(dateFilter.values[0]) : null,
          beforeDate: dateFilter.values
            ? parseDate(dateFilter.values[1])
            : null,
        };
      case "BetweenDaysAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(dateFilter.values?.[0]),
          lessThanValue: Number(dateFilter.values?.[1]),
          timeUnit: "days" as const,
        };
      case "BetweenMonthsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(dateFilter.values?.[0]),
          lessThanValue: Number(dateFilter.values?.[1]),
          timeUnit: "months" as const,
        };
      case "BetweenYearsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(dateFilter.values?.[0]),
          lessThanValue: Number(dateFilter.values?.[1]),
          timeUnit: "years" as const,
        };
      case "After":
        return {
          ...defaultValues,
          afterDate: dateFilter.values ? parseDate(dateFilter.values[0]) : null,
        };
      case "Before":
        return {
          ...defaultValues,
          beforeDate: dateFilter.values
            ? parseDate(dateFilter.values[0])
            : null,
        };
      default:
        return defaultValues;
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(),
  });

  useReportDirty(isDirty, setIsDirty);

  const greaterThanValue = watch("greaterThanValue");
  const lessThanValue = watch("lessThanValue");
  const afterDate = watch("afterDate");
  const beforeDate = watch("beforeDate");
  const isRelativeMode = useMemo(
    () => greaterThanValue !== null || lessThanValue !== null,
    [greaterThanValue, lessThanValue],
  );
  const isDateMode = useMemo(
    () => afterDate !== null || beforeDate !== null,
    [afterDate, beforeDate],
  );

  // Use useRef to track the previous values for detecting actual mode changes
  const prevRelativeModeRef = useRef(isRelativeMode);
  const prevDateModeRef = useRef(isDateMode);

  useEffect(() => {
    // Check for actual mode switching
    if (isRelativeMode && !prevRelativeModeRef.current && isDateMode) {
      // User switched to relative mode while date mode was active
      setValue("afterDate", null);
      setValue("beforeDate", null);
    } else if (isDateMode && !prevDateModeRef.current && isRelativeMode) {
      // User switched to date mode while relative mode was active
      setValue("greaterThanValue", null);
      setValue("lessThanValue", null);
    }

    // Update refs for next comparison
    prevRelativeModeRef.current = isRelativeMode;
    prevDateModeRef.current = isDateMode;
  }, [isRelativeMode, isDateMode, setValue]);

  const submit = handleSubmit((data) => {
    const filter: Omit<Partial<QueryFilter>, keyof FilterIdentifier> = {};

    // Check if we're using the relative mode
    if (data.greaterThanValue !== null || data.lessThanValue !== null) {
      if (data.greaterThanValue !== null && data.lessThanValue !== null) {
        console.debug("how the f we get here?");
        filter.operator = `Between${upperFirst(data.timeUnit)}Ago` as const;
        filter.values = [
          data.greaterThanValue.toString(),
          data.lessThanValue.toString(),
        ];
      } else if (data.greaterThanValue !== null) {
        filter.operator = `MoreThan${upperFirst(data.timeUnit)}Ago` as const;
        filter.values = [data.greaterThanValue.toString()];
      } else if (data.lessThanValue !== null) {
        filter.operator = `LessThan${upperFirst(data.timeUnit)}Ago` as const;
        filter.values = [data.lessThanValue.toString()];
      }
    }
    // Otherwise use date mode
    else if (data.afterDate !== null || data.beforeDate !== null) {
      if (data.afterDate && data.beforeDate) {
        // If both dates are provided, use Between
        filter.operator = "Between" as const;
        filter.values = [data.afterDate.toString(), data.beforeDate.toString()];
      } else if (data.afterDate) {
        // Only after date
        filter.operator = "After" as const;
        filter.values = [data.afterDate.toString()];
      } else if (data.beforeDate) {
        // Only before date
        filter.operator = "Before" as const;
        filter.values = [data.beforeDate.toString()];
      }
    } else {
      alert("Mindst et felt skal udfyldes");
      return;
    }

    if (filter.operator && filter.values) {
      update({
        value: {
          operator: filter.operator,
          values: filter.values,
        },
      });
    }
  });

  return (
    <div className="flex flex-col gap-y-9">
      <SegmentHeader title={props.title} description={props.description} />
      <Form
        id={formId}
        onSubmit={submit}
        className="flex h-full flex-col items-center gap-y-12"
      >
        <div className="flex w-full gap-4">
          <div className="relative">
            <NumberField
              control={control}
              name="greaterThanValue"
              placeholder="Mere end"
              aria-label="Mere end"
              className="flex-1"
            />
            {errors.greaterThanValue && (
              <div className="absolute -bottom-5 mt-2 ml-[5px] text-xs text-red-500">
                {errors.greaterThanValue.message}
              </div>
            )}
          </div>
          <div className="relative">
            <NumberField
              control={control}
              name="lessThanValue"
              placeholder="Mindre end"
              aria-label="Mindre end"
              className="flex-1"
            />
            {errors.lessThanValue && (
              <div className="absolute -bottom-5 mt-2 ml-[5px] text-xs text-red-500">
                {errors.lessThanValue.message}
              </div>
            )}
          </div>
          <Controller
            control={control}
            name="timeUnit"
            render={({ field, fieldState: { error } }) => (
              <Select
                placeholder="Enhed"
                aria-label="Enhed"
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                items={timeUnits}
                errorMessage={error?.message}
              >
                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
              </Select>
            )}
          />
        </div>
        <div className="text-sm font-medium text-neutral-400">eller</div>
        <div className="flex w-full gap-4 *:w-1/2">
          <Controller
            control={control}
            name="afterDate"
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                aria-label="Adresseændring efter"
                label="Efter den"
              />
            )}
          />
          <Controller
            control={control}
            name="beforeDate"
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                aria-label="Adresseændring før"
                label="Før den"
              />
            )}
          />
        </div>
      </Form>
    </div>
  );
}
